// Helpers shared by the global settings page and the per-service Settings tab.
// Both surfaces render the same Field contract:
//   { key, label, type, required, default, value, persisted, description?, options? }

// The backend surfaces a stored value that has dropped out of the registry as an
// extra option labelled "<VALUE> (unrecognised)" so the screen can show what is
// actually in force. Re-submitting it is rejected with a 400, so we treat it as
// display-only and block the save client-side.
const UNRECOGNISED_LABEL = /\(unrecognised\)\s*$/i;

export const isUnrecognisedOption = (option) =>
  !!option &&
  typeof option === 'object' &&
  typeof option.label === 'string' &&
  UNRECOGNISED_LABEL.test(option.label);

// True when the value currently shown for a select is one the registry no longer
// recognises.
export const isUnrecognisedValue = (field, value) =>
  field.type === 'select' &&
  (field.options || []).some(
    (option) => isUnrecognisedOption(option) && option.value === value
  );

// Flattens a loaded subject's groups into a single list of fields.
export const flattenFields = (subjectData) => {
  if (!subjectData || !subjectData.subject) return [];
  return (subjectData.subject.groups || []).reduce(
    (acc, group) => acc.concat(group.fields || []),
    []
  );
};

// A json field accepts an object/array; a string is parsed server-side but a
// malformed one 400s, so parse it here to fail early with a better message.
export const parseJsonValue = (value) => {
  if (typeof value !== 'string') return { ok: true, value };
  if (value.trim() === '') return { ok: true, value: null };
  try {
    return { ok: true, value: JSON.parse(value) };
  } catch (err) {
    return { ok: false, message: err.message };
  }
};

// Client-side checks run before submitting a batch. Returns an error string, or
// null when the pending changes look submittable.
export const validateChanges = (fields, changed) => {
  for (const change of changed) {
    const field = fields.find((f) => f.key === change.key);
    if (!field) continue;

    const isEmpty = change.value === '' || change.value == null;
    if (field.required && isEmpty) {
      return `${field.label} is required.`;
    }
    if (
      field.type === 'number' &&
      (change.value === '' || isNaN(Number(change.value)))
    ) {
      return `${field.label} expects a numeric value.`;
    }
    if (isUnrecognisedValue(field, change.value)) {
      return `${field.label} is set to "${change.value}", which is no longer a recognised option. Pick a valid option before saving.`;
    }
    if (field.type === 'json') {
      const parsed = parseJsonValue(change.value);
      if (!parsed.ok) {
        return `${field.label} is not valid JSON: ${parsed.message}`;
      }
    }
  }
  return null;
};

// Values already matching what is stored are skipped rather than rewritten, so
// meta.updated is legitimately allowed to be shorter than what we submitted.
// Describes that outcome without making it read like a partial failure.
export const describeSaveResult = (message, submitted, meta) => {
  // Only editorialise when the server actually reported what it wrote.
  if (!meta || !Array.isArray(meta.updated)) return message;

  const updated = meta.updated;
  const skipped = submitted.length - updated.length;

  if (skipped > 0 && updated.length > 0) {
    return `${message} (${updated.length} updated, ${skipped} already up to date)`;
  }
  if (skipped > 0 && updated.length === 0) {
    return `${message} (no changes needed — values already up to date)`;
  }
  return message;
};

// The error envelopes carry no `data` key, so pull the message out and append the
// per-field detail when the server names the offending keys.
export const formatApiError = (err, fallback = 'Something went wrong.') => {
  if (!err) return fallback;
  if (typeof err === 'string') return err;

  const base = err.message || fallback;
  const errors = Array.isArray(err.errors) ? err.errors : [];
  if (!errors.length) return base;

  const detail = errors
    .map((e) => (e.field ? `${e.field}: ${e.message}` : e.message))
    .filter(Boolean)
    .join('; ');

  return detail ? `${base} — ${detail}` : base;
};

// The maker-checker / 2fa interceptor already opened a modal for these, so they
// are not a save failure to report inline.
export const isApprovalHandled = (err) =>
  !!err &&
  typeof err === 'object' &&
  (err.__approvalHandled || err.status === 'approval-required');

export const isNotFound = (err) =>
  !!err && (err.status === 'not-found' || err.statusCode === 404);

export const isForbidden = (err) =>
  !!err && (err.status === 'forbidden' || err.statusCode === 403);
